import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { useTranslator } from '@/hooks/useTranslator';
import { noop } from '@/utility/noop';

interface Props {
  readonly id?: string;
  readonly name?: string;
  readonly defaultValue?: boolean;

  onChange?(value: boolean): void;
}

export const BooleanFormFieldInput: React.FC<Props> = (
  props: React.PropsWithChildren<Props>,
): React.ReactElement => {
  const { id, name, defaultValue = true, onChange = noop } = props;

  const [value, setValue] = React.useState<boolean>(defaultValue);

  const tr = useTranslator();
  const setToTrue = React.useCallback((): void => {
    setValue(true);
    onChange(true);
  }, [onChange]);

  const setToFalse = React.useCallback((): void => {
    setValue(false);
    onChange(false);
  }, [onChange]);

  return (
    <>
      <input name={name} type="hidden" value={value ? 'true' : 'false'} />

      <RadioGroup>
        <Box id={id} sx={styles.container}>
          <Box width="100%">
            <FormControlLabel
              sx={styles.control}
              control={
                <Box ml={-1}>
                  <Radio value="true" checked={Boolean(value)} tabIndex={0} onChange={setToTrue} />
                </Box>
              }
              label={
                <Typography fontSize={16} whiteSpace="nowrap" color="text.primary">
                  {tr('YES')}
                </Typography>
              }
            />
          </Box>
          <Box width="100%">
            <FormControlLabel
              sx={styles.control}
              control={
                <Box ml={-1}>
                  <Radio value="false" checked={!value} tabIndex={1} onChange={setToFalse} />
                </Box>
              }
              label={
                <Typography fontSize={16} whiteSpace="nowrap" color="text.primary">
                  {tr('NO')}
                </Typography>
              }
            />
          </Box>
        </Box>
      </RadioGroup>
    </>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'grid',
    columnGap: 3,
    gridTemplateColumns: 'repeat(2, 248px)',
  },
  control: {
    display: 'flex',
    alignItems: 'center',
    margin: 0,
  },
};
